'use client';

import * as React from 'react';
import { usePathname } from 'next/navigation';
import posthog from 'posthog-js';
import { AppBadgeBold } from '@/features/app-badge/primitives/AppBadgeBold';
import '@/features/app-badge/tokens';
import { useIsMobile } from '@/hooks/use-mobile';
import { useDeviceOS } from '@/hooks/use-device-os';
import { STORE_URLS } from '@/components/ui/app-store-badges';
import { AnalyticsEvent } from '@/shared/lib/analytics/events';
import { useTourActive } from '@/shared/lib/tours/tour-store';
import {
  APP_BADGE_CLOSED_EVENT,
  APP_BADGE_DISMISS_COOKIE,
  hasCookie,
  isPlaybackRoute,
  useWhatsNewHoldsPromptSlot,
  writeCookie,
} from './prompt-coordination';

const COOKIE_MAX_AGE = 60 * 60 * 24 * 7; // 7 days
const SHOW_DELAY_MS = 2500;

/**
 * Mobile-web app-install badge host. Mounts the closed-world `<AppBadgeBold>`
 * primitive when every gate passes:
 *   1. Mobile viewport (matchMedia < 768px)
 *   2. iOS or Android UA
 *   3. No `tt_appbadge_dismissed` cookie
 *   4. Pathname is not a video / shorts / course / series playback route
 *   5. What's New is not deciding or showing (shared prompt slot)
 *   6. 2.5 second delay after gates 1–5 settle
 *
 * Mounted only inside the authenticated branch of (platform)/layout.tsx.
 * Studio routes are auto-excluded (live outside the (platform) group).
 */
export function AppBadgeHost() {
  const isMobile = useIsMobile();
  const os = useDeviceOS();
  const pathname = usePathname();
  const whatsNewHoldsSlot = useWhatsNewHoldsPromptSlot();
  const tourActive = useTourActive();

  const [dismissed, setDismissed] = React.useState(false);
  const [visible, setVisible] = React.useState(false);
  const shownRef = React.useRef(false);

  React.useEffect(() => {
    if (hasCookie(APP_BADGE_DISMISS_COOKIE)) setDismissed(true);
  }, []);

  const suppressed = React.useMemo(
    () => (typeof pathname === 'string' ? isPlaybackRoute(pathname) : true),
    [pathname]
  );

  const eligible =
    isMobile && os !== null && !dismissed && !suppressed && !whatsNewHoldsSlot && !tourActive;

  React.useEffect(() => {
    if (!eligible) {
      setVisible(false);
      return;
    }
    const timer = window.setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [eligible, pathname]);

  React.useEffect(() => {
    if (visible && os !== null && !shownRef.current) {
      shownRef.current = true;
      posthog.capture(AnalyticsEvent.APP_BADGE_SHOWN, { os, pathname });
    }
  }, [visible, os, pathname]);

  if (!eligible || !visible || os === null) return null;

  const handleDismiss = () => {
    posthog.capture(AnalyticsEvent.APP_BADGE_DISMISSED, { os, pathname });
    writeCookie(APP_BADGE_DISMISS_COOKIE, COOKIE_MAX_AGE);
    setDismissed(true);
    setVisible(false);
    // Hand off to the location prompt: it waits on mobile until the badge closes.
    window.dispatchEvent(new Event(APP_BADGE_CLOSED_EVENT));
  };

  const handleCtaClick = () => {
    posthog.capture(AnalyticsEvent.APP_BADGE_STORE_CLICKED, { os, pathname });
    const url = STORE_URLS[os];
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 flex justify-center px-3 pb-3"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 12px)' }}
    >
      <AppBadgeBold os={os} tone="light" onDismiss={handleDismiss} onCtaClick={handleCtaClick} />
    </div>
  );
}
